import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { EVENT_TYPES, ATTENDANCE_RANGES } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  event_name: "",
  event_type: "",
  event_location: "",
  event_date: "",
  estimated_attendance: "",
  description: "",
  company_website: "",
};

const fieldClass =
  "bg-[#0a0a0a] border border-white/10 text-white rounded-none h-12 px-4 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] placeholder:text-white/30 transition-colors";

export const InquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "company", "email", "phone", "event_name", "event_type", "event_location", "estimated_attendance", "description"];
    for (const k of required) {
      if (!form[k].trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Inquiry received. Our team will be in touch shortly.");
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many submissions. Please try again in a few minutes.");
      } else {
        toast.error("Something went wrong. Please try again or email us directly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" data-testid="inquiry-section" className="relative py-24 md:py-36 border-t border-white/5 scroll-mt-24">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <div className="text-center mb-14">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Event Inquiry</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
              Bring Good Shot
              <span className="block text-[#D4AF37]">To Your Event</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl mx-auto text-white/55 font-light">
              Every activation is custom. Tell us about the opportunity and our team will build the right
              program for your venue.
            </p>
          </Reveal>
        </div>

        {done ? (
          <Reveal>
            <div data-testid="inquiry-success" className="border border-[#D4AF37]/30 bg-[#0a0a0a] p-12 text-center">
              <div className="mx-auto w-14 h-14 flex items-center justify-center border border-[#D4AF37] rounded-full">
                <Check className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="mt-6 font-display font-bold uppercase tracking-wide text-white text-2xl">Inquiry Received</h3>
              <p className="mt-3 text-white/60 font-light">Thank you. We'll be in touch shortly to talk through your event.</p>
              <button
                data-testid="inquiry-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] uppercase tracking-[0.16em] text-xs font-bold px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors"
              >
                Submit Another Inquiry
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form data-testid="inquiry-form" onSubmit={submit} className="grid md:grid-cols-2 gap-6">
              {/* Honeypot: hidden from users, catches bots. Do not remove. */}
              <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.company_website}
                onChange={set("company_website")}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <Field label="Name *"><Input data-testid="input-name" className={fieldClass} value={form.name} onChange={set("name")} placeholder="Your name" /></Field>
              <Field label="Company / Organization *"><Input data-testid="input-company" className={fieldClass} value={form.company} onChange={set("company")} placeholder="Company or organization" /></Field>
              <Field label="Email *"><Input data-testid="input-email" type="email" className={fieldClass} value={form.email} onChange={set("email")} placeholder="you@company.com" /></Field>
              <Field label="Phone *"><Input data-testid="input-phone" className={fieldClass} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" /></Field>
              <Field label="Event Name *"><Input data-testid="input-event-name" className={fieldClass} value={form.event_name} onChange={set("event_name")} placeholder="Name of the event" /></Field>

              <Field label="Event Type *">
                <Select value={form.event_type} onValueChange={setVal("event_type")}>
                  <SelectTrigger data-testid="select-event-type" className={`${fieldClass} justify-between`}>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[#D4AF37]/15 focus:text-white rounded-none">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Event Location *"><Input data-testid="input-location" className={fieldClass} value={form.event_location} onChange={set("event_location")} placeholder="City, venue or region" /></Field>
              <Field label="Event Date"><Input data-testid="input-date" type="date" className={`${fieldClass} [color-scheme:dark]`} value={form.event_date} onChange={set("event_date")} /></Field>

              <Field label="Estimated Attendance *">
                <Select value={form.estimated_attendance} onValueChange={setVal("estimated_attendance")}>
                  <SelectTrigger data-testid="select-attendance" className={`${fieldClass} justify-between`}>
                    <SelectValue placeholder="Select attendance" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                    {ATTENDANCE_RANGES.map((r) => (
                      <SelectItem key={r} value={r} className="focus:bg-[#D4AF37]/15 focus:text-white rounded-none">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Describe The Opportunity *">
                  <Textarea
                    data-testid="input-description"
                    className={`${fieldClass} h-auto min-h-[140px] py-3`}
                    value={form.description}
                    onChange={set("description")}
                    placeholder="Tell us about the event, goals, timing and what you're looking for from Good Shot."
                  />
                </Field>
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <button
                  data-testid="inquiry-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black uppercase tracking-[0.16em] text-sm font-bold px-8 py-4 hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                  {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />}
                </button>
                <p className="text-white/40 text-xs uppercase tracking-[0.14em]">No pricing or instant booking · Each activation is custom</p>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-[11px] uppercase tracking-[0.2em] text-white/55">{label}</Label>
    {children}
  </div>
);
